import type { SubscriptionStatus } from "@/types";
import type {
  OnboardingMode,
  SectorAccessState,
} from "@/core/hooks/useSectorAccessState";
import type { BlockReason } from "@/features/billing/components/AccessBlockedScreen";

// Status que mantêm o acesso liberado (BIL-38).
const ALLOWED_STATUSES: ReadonlyArray<SubscriptionStatus> = [
  "trialing",
  "active",
  "past_due",
];

/**
 * Modos que exigem cartão antes de liberar o produto (gate duro). Mora aqui, e
 * não no módulo do hook, porque o hook é mockado por vários testes de billing —
 * um helper exportado de lá obrigaria cada mock a reexportá-lo.
 */
function requiresCardUpfront(mode: OnboardingMode | null): boolean {
  return mode === "with_card" || mode === "direct_payment";
}

/**
 * Arranjos de cobrança que dispensam assinatura paga no Stripe (SA-1298):
 * parceria e cobrança que acontece fora desta assinatura (Pix, boleto, ou o
 * contrato de outra Company do mesmo cliente).
 *
 * Fail-safe: `null` (Company anterior ao campo) e valor desconhecido seguem o
 * caminho normal de cobrança. Na dúvida, cobra.
 */
function grantsAccessWithoutStripe(access: SectorAccessState): boolean {
  return (
    access.billing_arrangement === "partner_free" ||
    access.billing_arrangement === "external"
  );
}

/**
 * Trial que já venceu, sem cartão anexado (SA-1298).
 *
 * Quem move `trialing` → `paused` é o Stripe (`trial_settings.end_behavior`).
 * Só que existem contas sem subscription no Stripe (onboarding abandonado antes
 * do SETUP) e trials esticados à mão no admin depois do Stripe já ter pausado:
 * nesses casos ninguém faz a transição e o acesso ficaria livre para sempre.
 *
 * O recorte "sem cartão" é o que evita bloquear pagante: entre o fim do trial e
 * o webhook de conversão a sub segue `trialing` com cartão anexado.
 *
 * `trial_expired` vem decidido pelo servidor de propósito. Como este gate manda
 * no acesso ao produto inteiro, comparar `trial_ends_at` com o `Date.now()` do
 * browser colocaria o relógio da máquina do usuário no comando do billing.
 */
function isTrialExpiredWithoutCard(access: SectorAccessState): boolean {
  if (access.subscription_status !== "trialing") return false;
  if (access.has_payment_method) return false;
  return access.trial_expired;
}

export type BillingGateResult =
  | { kind: "allow" }
  | { kind: "onboarding" }
  | { kind: "blocked"; reason: BlockReason };

// BIL-39: mapeia status (+ vencimento do trial) no motivo de bloqueio.
// `canceled` e trial vencido sem cartão saem antes, com motivo próprio.
function deriveBlockReason(
  status: SubscriptionStatus,
  trialExpired: boolean
): BlockReason {
  if (status === "paused") {
    return trialExpired ? "trial-expired" : "subscription-paused";
  }
  if (status === "incomplete_expired" || status === "unpaid") {
    return "payment-issue";
  }
  return "unknown";
}

/**
 * Decide o gate de billing de um sector a partir do `access-state`. Fonte única
 * compartilhada pelo `SectorAccessGuard` (que redireciona para a página de gate
 * quando ≠ allow) e pela `BillingGatePage` (que renderiza a tela apropriada e
 * redireciona de volta ao produto quando volta a `allow`).
 *
 * Ordem: kill switch → cancelamento explícito → bypasses (interno / parceria /
 * sem subscription) → cobrança de ativação recusada → gate de onboarding "com
 * cartão" → trial vencido → allowlist de status → bloqueio.
 */
export function resolveBillingGate(
  access: SectorAccessState
): BillingGateResult {
  // Fail-open + kill switch (inclui loading/erro do endpoint → default fail-open).
  if (!access.enforcement_enabled) return { kind: "allow" };

  // SA-1298: `canceled` não é isentável por conta interna. A isenção de
  // `is_internal` existe para o enforcement automático de billing (fim de
  // trial, dunning) não atingir conta da Ginga; cancelar é ato deliberado de um
  // operador sobre AQUELA subscription, e é o sinal mais específico que existe.
  // Antes desta ordem, cancelar no admin uma company marcada como interna não
  // tirava o acesso, e nada na tela dizia por quê.
  if (access.subscription_status === "canceled") {
    return { kind: "blocked", reason: "subscription-canceled" };
  }

  // Isenção de contas internas (BIL-36).
  if (access.is_internal) return { kind: "allow" };
  // Parceria ou cobrança fora do Stripe (SA-1298).
  if (grantsAccessWithoutStripe(access)) return { kind: "allow" };
  // Degradação graciosa: sem subscription, não bloqueia (BIL-37).
  if (!access.subscription_status) return { kind: "allow" };

  // SA-1371: a cobrança que libera a conta foi recusada. `past_due` está na
  // allowlist abaixo para dar graça a quem JÁ pagou e atrasou uma renovação;
  // aqui não houve pagamento nenhum, então a mesma graça viraria produto de
  // graça. Fica ANTES do gate de onboarding porque o cartão JÁ está salvo (foi
  // ele que o Stripe recusou), e a tela de planos só pede cartão de novo.
  if (access.activation_payment_failed) {
    return { kind: "blocked", reason: "payment-declined" };
  }

  // Gate de onboarding "com cartão" (BFC-31..BFC-35): trial sem cartão nos modos
  // que exigem cartão na entrada → tela de planos (hard gate). Fica ANTES da
  // allowlist porque `trialing` passaria direto.
  //
  // SA-1218: `direct_payment` usa o MESMO gate. É ele que faz o trial do modo 3
  // ser invisível ao lead: a sub nasce `trialing` (placeholder técnico), mas o
  // produto fica barrado até o cartão entrar, e aí a cobrança sai na hora.
  if (
    requiresCardUpfront(access.onboarding_mode) &&
    !access.has_payment_method &&
    access.subscription_status === "trialing"
  ) {
    return { kind: "onboarding" };
  }

  // Fica ANTES da allowlist porque `trialing` passaria direto.
  if (isTrialExpiredWithoutCard(access)) {
    return { kind: "blocked", reason: "trial-expired" };
  }

  if (ALLOWED_STATUSES.includes(access.subscription_status)) {
    return { kind: "allow" };
  }

  return {
    kind: "blocked",
    reason: deriveBlockReason(access.subscription_status, access.trial_expired),
  };
}
